import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { track } from '@quoter/web-core'
import { db } from '@/lib/db'
import type { DiaryEntry, Meal, SymptomLog } from '@/lib/db-types'
import { useMe } from './useMe'

const entriesKey = (userId: string, date: string) => ['lowfodmaps', 'diary', userId, date] as const
const symptomsKey = (userId: string) => ['lowfodmaps', 'symptoms', userId] as const

/** What was eaten on one day, in the order it was logged. */
export function useDiaryDay(date: string) {
  const me = useMe()
  const query = useQuery({
    queryKey: entriesKey(me?.userId ?? '__none', date),
    enabled: !!me,
    queryFn: async (): Promise<DiaryEntry[]> => {
      const { data, error } = await db
        .from('diary_entries')
        .select('*')
        .eq('organization_id', me!.orgId)
        .eq('user_id', me!.userId)
        .eq('entry_date', date)
        .order('created_at')
      if (error) throw error
      return data ?? []
    },
  })
  return { entries: query.data ?? [], loading: query.isLoading, error: query.error }
}

export function useAddEntry(date: string) {
  const me = useMe()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { meal: Meal; body: string }) => {
      if (!me) throw new Error('not_signed_in')
      const { error } = await db.from('diary_entries').insert({
        organization_id: me.orgId,
        user_id: me.userId,
        entry_date: date,
        meal: input.meal,
        body: input.body,
      })
      if (error) throw error
    },
    onSuccess: () => {
      // A count of entries, never their content.
      track('diary_entry_added')
      if (me) void queryClient.invalidateQueries({ queryKey: entriesKey(me.userId, date) })
    },
  })
}

export function useRemoveEntry(date: string) {
  const me = useMe()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      if (!me) throw new Error('not_signed_in')
      // Owner clauses repeated beside the id: the id alone is not a permission.
      const { error } = await db
        .from('diary_entries')
        .delete()
        .eq('id', id)
        .eq('organization_id', me.orgId)
        .eq('user_id', me.userId)
      if (error) throw error
    },
    onSuccess: () => {
      if (me) void queryClient.invalidateQueries({ queryKey: entriesKey(me.userId, date) })
    },
  })
}

/**
 * The daily check-ins for a range of days, as a map by date. One query feeds
 * both the day's own check-in and the week strip.
 */
export function useSymptoms(from: string, to: string) {
  const me = useMe()
  const query = useQuery({
    queryKey: [...symptomsKey(me?.userId ?? '__none'), from, to],
    enabled: !!me,
    queryFn: async (): Promise<Record<string, SymptomLog>> => {
      const { data, error } = await db
        .from('symptom_logs')
        .select('*')
        .eq('organization_id', me!.orgId)
        .eq('user_id', me!.userId)
        .gte('log_date', from)
        .lte('log_date', to)
      if (error) throw error
      return Object.fromEntries((data ?? []).map((row) => [row.log_date, row]))
    },
  })
  return { byDate: query.data ?? {}, loading: query.isLoading }
}

export function useSaveSymptom() {
  const me = useMe()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { date: string; score: number; note: string }) => {
      if (!me) throw new Error('not_signed_in')
      const { error } = await db.from('symptom_logs').upsert(
        {
          organization_id: me.orgId,
          user_id: me.userId,
          log_date: input.date,
          score: input.score,
          note: input.note,
        },
        { onConflict: 'organization_id,user_id,log_date' },
      )
      if (error) throw error
    },
    onSuccess: () => {
      // That a check-in happened — not the score.
      track('symptom_logged')
      if (me) void queryClient.invalidateQueries({ queryKey: symptomsKey(me.userId) })
    },
  })
}
